// Receiving and stock mutations, spread into `mutations` (shared/mutations.ts). Implementations:
// server/src/mutations/receiving.ts; the Shopify side: server/src/shopify/incoming.ts.
import { z } from "zod";
import { custom, STAFF } from "./mutation-kit.ts";
import { REJECT_REASONS } from "./receiving.ts";

const RECEIVERS = ["admin", "inventory", "receiving"] as const;

const text = (max = 200) => z.string().trim().max(max);
/** Stock changes --- Shopify's states a count can be changed in (committed is customer orders': never). */
export const STOCK_STATES = ["available", "damaged", "quality_control", "safety_stock", "reserved"] as const;
export const STOCK_STATE_LABELS: Record<(typeof STOCK_STATES)[number], string> = {
  available: "Available",
  damaged: "Damaged",
  quality_control: "Quality control",
  safety_stock: "Safety stock",
  reserved: "Reserved",
};
const units = z.number().nonnegative("Counts are 0 or more").max(10_000_000);

export const receiptLineInput = z.object({
  po_line_id: z.uuid(),
  /** Good units, into stock. */
  qty_received: units.optional(),
  /** Damaged or wrong units, kept out of stock. */
  qty_rejected: units.optional(),
  reject_reason: z.enum(["", ...REJECT_REASONS]).optional(),
  photo_url: text(1000).optional(),
  notes: text(2000).optional(),
  /** Where the good units were put away; the location's default bin if left out. */
  bin_id: z.uuid().optional(),
  /** The line's received total the screen showed while counting; if a receipt was posted since, the server asks to check. */
  seen_received: z.number().nonnegative().optional(),
});

export const receivingMutations = {
  /**
   * Posts one delivery's counts against a PO: PO lines, stock moves for local items, Shopify items queued for
   * the PO's incoming shipment, and the PO's status. The browser makes the id, so posting twice is a no-op.
   */
  "receipt.post": custom(
    z.object({
      id: z.uuid(),
      po_id: z.uuid(),
      lines: z.array(receiptLineInput).min(1, "Count something first").max(500),
      notes: text(5000).optional(),
      /** Nothing else is coming: the PO is received even if short (Shopify's remaining incoming is cancelled). */
      closes_po: z.boolean().optional(),
      /** Confirms counts above what was ordered. */
      allow_over: z.boolean().optional(),
    }),
    RECEIVERS,
  ),
  /** Sends a receipt Shopify refused (or couldn't be reached for) again. */
  "receipt.retry_shopify": custom(z.object({ id: z.uuid() }), RECEIVERS),
  /** Shows a sent PO's Shopify items as incoming in Shopify now (or again, after a failure). */
  "po.push_incoming": custom(z.object({ id: z.uuid() }), STAFF),
  /**
   * A local item's stock at a location: `adjust` adds `qty` (negative takes away), `count` sets it to `qty`
   * (the move is the difference). The id makes posting twice safe.
   */
  /**
   * Shopify's counts changed from HHL Ops (migration 065): an item's changes at one HHL location, saved together and sent
   * in order (moves, then the set): `set` the available count to `qty`, or `move` `qty` units from one state to another.
   * `seen`: the count the person saw (the set's available after the moves before it; a move's from state), so Shopify
   * refuses if it changed meanwhile. The ids make saving twice safe. Admin and inventory.
   */
  "stock.change_shopify": custom(
    z.object({
      item_id: z.uuid(),
      location_id: z.uuid(),
      changes: z
        .array(
          z.object({
            id: z.uuid(),
            kind: z.enum(["set", "move"]),
            from_state: z.enum(STOCK_STATES).optional(),
            to_state: z.enum(STOCK_STATES).optional(),
            qty: z.number().int("Whole units").min(0, "Counts are 0 or more").max(10_000_000),
            seen: z.number().int().nullable(),
          }),
        )
        .min(1)
        .max(10),
    }),
    STAFF,
  ),
  /** Sends a stock change Shopify refused (or couldn't be reached for) again, as it was. */
  "stock_change.retry": custom(z.object({ id: z.uuid() }), STAFF),
  "stock.adjust": custom(
    z.object({
      id: z.uuid(),
      item_id: z.uuid(),
      location_id: z.uuid(),
      kind: z.enum(["adjust", "count"]),
      qty: z.number().min(-10_000_000).max(10_000_000),
      notes: text(1000).optional(),
      /** The bin counted or changed; the location's default bin if left out. */
      bin_id: z.uuid().optional(),
    }),
    STAFF,
  ),
} as const;
